import { type FC, type ReactNode, useState, useCallback, type ChangeEvent } from 'react';
import { FormattedMessage, useIntl, defineMessages } from 'dibs-react-intl';
import { utils } from 'ethers';

import { AutocompleteInput } from 'dibs-elements/exports/AutocompleteInput.tsx';
import { Spinner } from 'dibs-elements/exports/Spinner.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';
import CloseIcon from 'dibs-icons/exports/legacy/Close.ts';
import { useWeb3Connection } from 'dibs-crypto-utils/exports/useWeb3Connection.ts';
import { shortAddress, etherscanUrl } from 'dibs-crypto-utils/exports/formattingUtils.ts';

import ActionModal, { type ActionModalProps } from './ActionModal.tsx';
import { EditionsCounter } from 'dibs-crypto-utils/exports/EditionsCounter.ts';
import { type TransferTokenStatus } from '../../hooks/useTransferToken.ts';

import styles from './TransferTokenModal.scss';

export const TRANSFER_TOKEN = 'TRANSFER_TOKEN';

export const transferModalCopy = defineMessages({
    placeholder: {
        id: 'nft.manage.transferNft.input.placeholder',
        defaultMessage: 'Enter wallet address',
    },
    initial: {
        id: 'nft.manage.transferNft.body.initial',
        defaultMessage:
            'Enter the wallet address of the person to which you’d like to transfer this token. Transfers cannot be canceled. Gas fees apply.',
    },
    initialTitle: {
        defaultMessage: 'Transfer',
        id: 'nft.manage.transferNft.header.initial',
    },
    success: {
        id: 'nft.manage.transferNft.body.success',
        defaultMessage:
            'Your {transferBalance, plural, one {NFT was} other {NFTs were}} transferred successfully to {address}. Once you close this window, we’ll redirect you to your profile.',
    },
    successTitle: {
        defaultMessage: 'Transferred',
        id: 'nft.manage.transferNft.header.success',
    },
    inProgress: {
        id: 'nft.manage.transferNft.body.inProgress',
        defaultMessage:
            'We are processing the transfer. This could take a few minutes. You do not need to leave this page open for the process to complete successfully.',
    },
    inProgressTitle: {
        defaultMessage: 'Transferring',
        id: 'nft.manage.transferNft.header.inProgress',
    },
    error: {
        id: 'nft.manage.transferNft.body.error',
        defaultMessage:
            'Unexpected error happened while processing the transaction. Please try again later.',
    },
    errorTitle: {
        defaultMessage: 'Transfer Error',
        id: 'nft.manage.transferNft.header.error',
    },
    inProgressToast: {
        id: 'nft.manage.transferNft.body.loadingToast',
        defaultMessage:
            "We are processing the transfer. This could take a few minutes. Once the process is complete, we'll redirect you to your profile.",
    },
    successToast: {
        id: 'nft.manage.transferNft.body.successToast',
        defaultMessage:
            'Your {affectedEditions, plural, one {NFT was} other {NFTs were}} transferred successfully.',
    },
    ownAddress: {
        id: 'nft.manage.transferNft.body.ownAddressError',
        defaultMessage: 'You cannot transfer editions to your own wallet',
    },
});

export type TransferProps = {
    status: TransferTokenStatus;
    onClick(address: string | null, editions: number): void;
    reset(): void;
    onClose: ActionModalProps['onClose'];
    nftItem: ActionModalProps['nftItem'];
    viewer: ActionModalProps['viewer'];
    currentBalance: number | null;
};

type Suggestion = { displayValue: ReactNode; value: string };

function validateAddress(value: string): { isAddress: boolean; isEnsName: boolean } {
    const isAddress = utils.isAddress(value);
    // no need for complex regex - rely on ethers to lookup address by ens
    const isEnsName = /.+\.eth$/.test(value);

    return { isAddress, isEnsName };
}

const TransferTokenModal: FC<TransferProps> = ({
    reset,
    status,
    onClick,
    currentBalance,
    ...actionModalProps
}) => {
    const { formatMessage } = useIntl();
    const { library, account } = useWeb3Connection();

    const [inputValue, setInputValue] = useState('');
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
    const [transferBalance, setTransferBalance] = useState<number | null>(1);
    const [autocompleteError, setAutocompleteError] = useState<boolean>(false);

    const validTransfer =
        currentBalance === null ||
        (transferBalance &&
            !isNaN(transferBalance) &&
            transferBalance >= 1 &&
            transferBalance <= currentBalance);

    const onInputChange = useCallback(
        async (e: ChangeEvent<HTMLInputElement>) => {
            setAutocompleteError(false);
            if (selectedAddress) {
                setSelectedAddress(null);
            }

            reset();

            const value = e.target.value;
            setInputValue(value);
            setIsLoadingSuggestions(true);
            let newSuggestions: Suggestion[] = [];

            const { isAddress, isEnsName } = validateAddress(value);
            if (isAddress) {
                const ensName = await library?.lookupAddress(value);

                if (ensName) {
                    newSuggestions = [
                        {
                            displayValue: (
                                <>
                                    <b>{ensName}</b> ({shortAddress(value)})
                                </>
                            ),
                            value,
                        },
                    ];
                } else {
                    newSuggestions = [{ displayValue: value, value }];
                }
            } else if (isEnsName) {
                const addressByEns = await library?.resolveName(value);

                if (addressByEns) {
                    newSuggestions = [
                        {
                            displayValue: (
                                <>
                                    <b>{value}</b> ({shortAddress(addressByEns)})
                                </>
                            ),
                            value: addressByEns,
                        },
                    ];
                }
            }

            setSuggestions(newSuggestions);
            setIsLoadingSuggestions(false);
        },
        [library, reset, selectedAddress]
    );

    const onBlurSetAddressIfValid = useCallback(async () => {
        const { isAddress, isEnsName } = validateAddress(inputValue);
        if (isAddress) {
            setSelectedAddress(inputValue);
        } else if (isEnsName) {
            const addressByEns = await library?.resolveName(inputValue);
            if (addressByEns) {
                setSelectedAddress(addressByEns);
            }
        }
    }, [inputValue, library]);

    const onSelectSuggestion = useCallback(
        (selected?: Suggestion | null) => {
            if (selected) {
                setSelectedAddress(selected.value);
                reset();
            }
        },
        [reset]
    );

    const getAutocompleteDecorator = (): ReactNode => {
        if (isLoadingSuggestions) {
            return <Spinner size="tiny" black />;
        } else if (selectedAddress) {
            return (
                <Link
                    className={styles.clearIconLink}
                    onClick={() => {
                        setSelectedAddress(null);
                        setInputValue('');
                        setAutocompleteError(false);
                    }}
                >
                    <CloseIcon className={styles.clearIcon} />
                </Link>
            );
        }
        return null;
    };

    const messageKey = status || 'initial';

    return (
        <ActionModal
            status={status}
            title={formatMessage(transferModalCopy[`${messageKey}Title`])}
            cta={
                !status ? (
                    <FormattedMessage
                        defaultMessage="Transfer"
                        id="nft.manage.transferNft.button"
                    />
                ) : undefined
            }
            ctaOnClick={() => {
                if (selectedAddress !== account) {
                    onClick(selectedAddress, transferBalance || 1);
                } else {
                    setAutocompleteError(true);
                }
            }}
            ctaDisabled={!selectedAddress || !validTransfer || status === 'error'}
            error={status === 'error' ? formatMessage(transferModalCopy.error) : null}
            {...actionModalProps}
        >
            {status !== 'error' && (
                <p className={styles.paragraph}>
                    {formatMessage(transferModalCopy[messageKey], {
                        transferBalance,
                        address: (
                            <span className={styles.boldText}>
                                {shortAddress(selectedAddress ?? '')}
                            </span>
                        ),
                    })}
                </p>
            )}
            {status === null && (
                <>
                    <AutocompleteInput<Suggestion>
                        value={inputValue}
                        options={suggestions}
                        dataTn="transfer-input"
                        name="transfer-input"
                        label={
                            <FormattedMessage
                                defaultMessage="Transfer"
                                id="nft.manage.transferNft.input.label"
                            />
                        }
                        rightDecorator={getAutocompleteDecorator()}
                        placeholder={formatMessage(transferModalCopy.placeholder)}
                        autoFocus
                        size="large"
                        onSelect={onSelectSuggestion}
                        onChange={onInputChange}
                        onBlur={onBlurSetAddressIfValid}
                        errorMessage={
                            autocompleteError ? formatMessage(transferModalCopy.ownAddress) : null
                        }
                    />

                    <div className={styles.linkContainer}>
                        {selectedAddress ? (
                            <Link
                                href={etherscanUrl('/address/' + selectedAddress)}
                                target="_blank"
                            >
                                <FormattedMessage
                                    defaultMessage="Etherscan"
                                    id="nft.manage.transferNft.etherscan"
                                />
                            </Link>
                        ) : null}
                    </div>
                </>
            )}
            {currentBalance && status === null && (
                <EditionsCounter
                    balance={transferBalance}
                    maxBalance={currentBalance}
                    setBalance={setTransferBalance}
                    label={
                        <FormattedMessage
                            defaultMessage="Quantity to Transfer"
                            id="nft.manage.transferNft.transferEditionsLabel"
                        />
                    }
                    maxBalanceMessage={
                        <>
                            <FormattedMessage
                                id="nft.manage.transferNft.maxEditions"
                                defaultMessage="<bold>You have {currentBalance, plural, one {# edition} other {# editions}} available to transfer.</bold>"
                                values={{
                                    bold: chunks => (
                                        <span className={styles.boldText}>{chunks}</span>
                                    ),
                                    currentBalance,
                                }}
                            />
                            {currentBalance > 1 && (
                                <>
                                    {' '}
                                    <FormattedMessage
                                        id="nft.manage.transferNft.maxEditions.cannotExceed"
                                        defaultMessage="The quantity cannot exceed {currentBalance}."
                                        values={{
                                            currentBalance,
                                        }}
                                    />
                                </>
                            )}
                        </>
                    }
                />
            )}
        </ActionModal>
    );
};

export default TransferTokenModal;
